// Stable `code` values the backend returns in error bodies.
export const apiErrorCodes = {
  USER_EXTENSION_NOT_FOUND: 'USER_EXTENSION_NOT_FOUND',
  EXTENSION_INCOMPLETE: 'EXTENSION_INCOMPLETE',
  MANAGER_UNAVAILABLE: 'MANAGER_UNAVAILABLE',
  MANAGER_REQUEST_FAILED: 'MANAGER_REQUEST_FAILED',
  AGENT_NOT_LOGGED_IN: 'AGENT_NOT_LOGGED_IN',
  QUEUEMETRICS_NOT_CONFIGURED: 'QUEUEMETRICS_NOT_CONFIGURED',
  QUEUEMETRICS_AGENT_NOT_FOUND: 'QUEUEMETRICS_AGENT_NOT_FOUND',
} as const;

// Retry-After is not CORS-exposed by default, so fall back to the backend's value.
const MANAGER_RETRY_AFTER_SECONDS = 10;

// RFC 9110 §10.2.3: delay-seconds or an HTTP-date.
export const parseRetryAfter = (value: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  // Date.parse also accepts bare numbers such as "-5"; an HTTP-date names a weekday.
  if (!/^[A-Za-z]{3},/.test(trimmed)) return undefined;
  const date = Date.parse(trimmed);
  return Number.isNaN(date)
    ? undefined
    : Math.max(0, Math.ceil((date - Date.now()) / 1000));
};

export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly code?: string,
    public readonly retryAfterSeconds?: number
  ) {
    super(code ? `HTTP ${status} ${code}` : `HTTP ${status}`);
    this.name = 'HttpError';
  }

  // Error bodies are optional and may not be JSON; only body read failures propagate.
  static async fromResponse(response: Response) {
    const text = await response.text();
    let code: string | undefined;
    try {
      const body: unknown = JSON.parse(text);
      if (
        body &&
        typeof body === 'object' &&
        'code' in body &&
        typeof body.code === 'string'
      ) {
        code = body.code;
      }
    } catch {
      code = undefined;
    }
    return new HttpError(
      response.status,
      code,
      parseRetryAfter(response.headers.get('Retry-After'))
    );
  }
}

// Permanent HTTP and schema errors need intervention, not another request.
export const shouldRetryQueueRequest = (failureCount: number, error: unknown) =>
  failureCount < 3 &&
  (error instanceof HttpError
    ? error.status >= 500 &&
      error.status < 600 &&
      error.code !== apiErrorCodes.MANAGER_REQUEST_FAILED
    : error instanceof TypeError ||
      (error instanceof Error &&
        (error.name === 'TimeoutError' || error.name === 'AbortError')));

const retryAfterSecondsOf = (error: unknown) =>
  error instanceof HttpError
    ? (error.retryAfterSeconds ??
      (error.code === apiErrorCodes.MANAGER_UNAVAILABLE
        ? MANAGER_RETRY_AFTER_SECONDS
        : undefined))
    : undefined;

// Honor the server's Retry-After as the backoff base when it gives one.
export const queueRetryDelay = (attempt: number, error?: unknown) => {
  const retryAfterSeconds = retryAfterSecondsOf(error);
  return retryAfterSeconds === undefined
    ? Math.min(1000 * 2 ** attempt, 8000)
    : retryAfterSeconds * 1000 * 2 ** attempt;
};
